import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  CartesianGrid,
  Tooltip,
  YAxis,
  ResponsiveContainer,
} from 'recharts';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatNumberToStringMonthWithoutYear } from '../../ClientDetail/ObjectiveChart';

interface IEntry {
  createdAt: string;
  [key: string]: string | number;
}

interface IDataEntry {
  createdAt: string;
  [key: string]: string | number;
}

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const month = date.toLocaleString('default', { month: 'short' });
  const day = date.getDate();
  return `${month} ${day}`;
};

export interface OriginalData {
  createdAt: string;
  value: string;
}

export interface TransformedData {
  createdAt: string;
  [key: string]: string | number;
}

export const transformData = (data: OriginalData[]): TransformedData[] => {
  const formattedData: { createdAt: string; value: number }[] = [];

  data.forEach((item) => {
    const key = formatNumberToStringMonthWithoutYear(item.createdAt);
    formattedData.push({ createdAt: key, value: parseFloat(Number(item.value).toFixed(1)) });
  });

  return formattedData.sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return dateA.getTime() - dateB.getTime();
  });
};

interface AdvancedTrackingActivityLineChartProps {
  objectiveProgress: {
    createdAt: string;
    value: string;
  }[];
}

function AdvancedTrackingActivityLineChart({
  objectiveProgress,
}: AdvancedTrackingActivityLineChartProps) {
  const [data, setData] = useState<IDataEntry[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    if (objectiveProgress.length === 0) return;
    setData(transformData(objectiveProgress));
  }, [objectiveProgress]);

  if (data.length === 0)
    return (
      <Box width="100%" height="262px" display="flex" alignItems="center" justifyContent="center">
        <Typography variant="h6" color="primary.light">
          {t('clientProgress.noData')}
        </Typography>
      </Box>
    );

  return (
    <div style={{ direction: 'ltr' }}>
      <ResponsiveContainer width="100%" height={262}>
        <LineChart data={data} margin={{ left: 0, top: 5, right: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="createdAt"
            tickLine={false}
            tick={{ fill: '#7CAEBE' }}
            stroke="#7CAEBE"
            style={{
              textShadow: '0px 4px 32px rgba(0, 0, 0, 0.07)',
              fontFamily: 'Satoshi',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '120%',
            }}
          />
          <YAxis
            tickLine={false}
            width={40}
            tick={{ fill: '#7CAEBE' }}
            stroke="#7CAEBE"
            style={{
              textAlign: 'right',
              textShadow: '0px 4px 32px rgba(0, 0, 0, 0.07)',
              fontFamily: 'Satoshi',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '120%',
            }}
          />
          <Tooltip />
          <Line dataKey="value" stroke="#FFD9BE" isAnimationActive={false} strokeWidth={3} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default React.memo(AdvancedTrackingActivityLineChart);
